/**
 * Staff client switcher — which client workspace a Beatly staff session acts in.
 *
 * Staff (BO admin token, enrolled CM, beatly.com login) land in the house team.
 * Picking a client here sends `X-Cowork-Team` on every Cowork API request, so
 * Brand OS, Reports, Media plans and Kickoff read and write THAT client's
 * workspace (backend: middleware/staffScope.ts). The backend ignores the header
 * for non-staff sessions.
 *
 * Storage: inside the BO frame (embed mode) the client and brand choice is kept
 * per frame/tab (sessionStorage). bo.* and omni.* are the same site, so writing
 * it to localStorage pushed a client's brand into the staff member's own omni
 * tabs. Outside the frame the brand choice stays in localStorage as before.
 *
 * Kept import-free so the API client can use it without a cycle.
 */

import { isEmbedMode } from '@/utils/urlParams';

const KEY = 'cowork.staffTeam';
export const HOUSE = 'house';

/** sessionStorage inside the BO frame (per frame/tab, never shared with normal omni tabs), localStorage otherwise. */
export function scopeStorage(): Storage {
  return isEmbedMode() ? sessionStorage : localStorage;
}

/** Selected client team id, `'house'` for the house workspace, or null (never chosen). */
/**
 * Only inside the back office (embed mode). In the omni app itself the platform's own team switch (top right) picks
 * the client and mints a token for it, so a second picker — and a stored header that could disagree with it — is not
 * used there. A BO admin session cannot switch teams on the platform, so the BO hands the client over instead.
 */
export function getStaffScopeTeam(): string | null {
  if (!isEmbedMode()) return null;
  // The client this page loaded with — never a newer value another tab wrote to the shared storage: a page's
  // requests (and saves) always go to the client it shows. A new client arrives by reloading the page.
  return loadedTeam() || null;
}

export function setStaffScopeTeam(teamId: string | null): void {
  try {
    const store = scopeStorage();
    if (teamId) store.setItem(KEY, teamId); else store.removeItem(KEY);
  } catch { /* storage unavailable: the session stays in its own team */ }
}

const ACTIVE_BRAND_KEY = 'cowork_active_brand_id';
const HOST_SCOPE_KEY = 'cowork.hostScope';

/** The sticky brand choice (per frame inside the BO, per browser otherwise). */
export function readStoredActiveBrand(): string | undefined {
  try { return scopeStorage().getItem(ACTIVE_BRAND_KEY) || undefined; } catch { return undefined; }
}

/** Store the sticky brand choice without notifying the pages (see setActiveBrand for that). */
export function writeStoredActiveBrand(brandId: string | null): void {
  try {
    const store = scopeStorage();
    if (brandId) store.setItem(ACTIVE_BRAND_KEY, brandId); else store.removeItem(ACTIVE_BRAND_KEY);
  } catch { /* storage unavailable */ }
}

/** Point the pages at a brand (CoworkPage and Brand OS follow the event without a reload). */
function setActiveBrand(brandId: string | null): void {
  writeStoredActiveBrand(brandId);
  window.dispatchEvent(new Event('cowork-active-brand-changed'));
}

/**
 * True inside the back office's Commercial suite: the BO's top bar owns the client and brand, so this app shows no
 * picker of its own (one control, one truth).
 */
export function hostOwnsScope(): boolean {
  try { return isEmbedMode() && sessionStorage.getItem(HOST_SCOPE_KEY) === '1'; } catch { return false; }
}

/**
 * A hosting shell (the Beatly BO frame) hands the client over in the URL: `?scopeTeam=<team id>` (or `house`),
 * `?scopeBrand=<brand id>`, and `?hostScope=1` when its own picker owns the choice (Commercial suite). Adopted once
 * per page load, before the first API call, then stored like a manual choice.
 */
export function adoptStaffScopeFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    const t = url.searchParams.get('scopeTeam');
    const b = url.searchParams.get('scopeBrand');
    const host = url.searchParams.get('hostScope');
    if (!t && !b && host === null) return;
    if (t) setStaffScopeTeam(t.trim() || null);
    // The host names the brand, or (its "All brands") none: then the pages fall back to the client's main brand
    // (useActiveBrand) instead of a brand remembered from another client or an earlier choice.
    if (b) setActiveBrand(b.trim() || null);
    else if (host === '1' && t) setActiveBrand(null);
    if (isEmbedMode()) { if (host === '1') sessionStorage.setItem(HOST_SCOPE_KEY, '1'); else sessionStorage.removeItem(HOST_SCOPE_KEY); }
    for (const k of ['scopeTeam', 'scopeBrand', 'hostScope']) url.searchParams.delete(k);
    pageTeam = t ? (t.trim() || null) : undefined; // this load's client is the one just adopted
    window.history.replaceState(window.history.state, '', url.toString());
  } catch { /* no window / bad URL: keep the stored choice */ }
}

/** The client this page loaded with (read once, after a host's `scopeTeam` was adopted). */
let pageTeam: string | null | undefined;
function loadedTeam(): string | null {
  if (pageTeam === undefined) {
    try { pageTeam = scopeStorage().getItem(KEY); } catch { pageTeam = null; }
  }
  return pageTeam;
}

/** The back office frames this app from its own origins only. */
function trustedHost(origin: string): boolean {
  try { const h = new URL(origin).hostname; return h === 'localhost' || h === '127.0.0.1' || h === 'beatly.com' || h.endsWith('.beatly.com'); } catch { return false; }
}

/**
 * The back office picked another client or brand while this frame is open (`postMessage` from the parent):
 * a brand switches in place; another client reloads the frame so every page re-reads its workspace.
 */
export function listenForHostScope(): () => void {
  loadedTeam(); // snapshot now, at page load — later the storage may hold another tab's choice
  const onMessage = (e: MessageEvent) => {
    if (e.source !== window.parent || !trustedHost(e.origin)) return;
    const d = e.data as { type?: unknown; team?: unknown; brand?: unknown } | null;
    if (!d || d.type !== 'beatly:client-scope' || typeof d.team !== 'string') return;
    const team = d.team.trim() || null;
    const brand = typeof d.brand === 'string' && d.brand.trim() ? d.brand.trim() : null;
    // Compared with the client THIS page loaded with, not with storage: frames in other tabs share the storage and
    // may already have written the new client, which would make this one skip its reload and keep the old client.
    if (team !== loadedTeam()) {
      setStaffScopeTeam(team);
      // A brand belongs to its client: with none named, the page picks the client's default brand.
      writeStoredActiveBrand(brand);
      window.location.reload();
      return;
    }
    // Same client: another brand, or "All brands" (none named → the client's main brand, see adoptStaffScopeFromUrl).
    setActiveBrand(brand);
  };
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

/** Header(s) to add to every Cowork API request. Empty for non-staff or no choice. */
export function staffScopeHeaders(): Record<string, string> {
  const t = getStaffScopeTeam();
  return t ? { 'X-Cowork-Team': t } : {};
}

/** True when the selection is a real Beatly client team (not house, not unset). */
export function isClientTeamId(id: string | null | undefined): id is string {
  return !!id && id !== HOUSE && /-tea$/.test(id) && !id.startsWith('admin-');
}
